import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, apiUrl, fmtFecha } from '../utils/api';
import { Spinner, Empty, StatusBadge, ConfirmModal } from '../components/UI';
import CerrarOrden from './CerrarOrden';
import EditarOrden from './EditarOrden';

export default function Ordenes() {
  const location = useLocation();
  const navigate = useNavigate();
  const idAbrir = location.state?.idAbrir;
  const idClienteOrigen = location.state?.idClienteOrigen;

  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detalle, setDetalle] = useState(null);
  const [cerrando, setCerrando] = useState(null);
  const [editando, setEditando] = useState(null);
  const [confirmandoEliminar, setConfirmandoEliminar] = useState(false);
  const [eliminandoDeLista, setEliminandoDeLista] = useState(null);
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const [emailEnviado, setEmailEnviado] = useState(false);
  const [busqueda, setBusqueda] = useState('');

  const cargar = () => {
    setLoading(true);
    api('/ordenes/').then(res => { setOrdenes(res); setLoading(false); });
  };

  useEffect(() => {
    cargar();
    if (idAbrir) verDetalle(idAbrir);
  }, []);

  const verDetalle = async (id) => {
    const res = await api(`/ordenes/${id}`);
    setDetalle(res);
    setEmailEnviado(false);
  };

  const enviarPorEmail = async (tipo) => {
    setEnviandoEmail(true);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000);
      const path = tipo === 'cerrada'
        ? `/descargas/ordenes/${detalle.id}/pdf/enviar-email`
        : `/descargas/ordenes/${detalle.id}/hoja-trabajo/enviar-email`;
      const res = await fetch(apiUrl(path), { method: 'POST', signal: controller.signal });
      clearTimeout(timeoutId);
      if (!res.ok) throw new Error('No se pudo enviar');
      setEmailEnviado(true);
    } catch {
      alert('No se pudo enviar el email. Puede que el servidor de correo esté tardando demasiado — probá de nuevo en un rato.');
    }
    setEnviandoEmail(false);
  };

  const volver = () => {
    if (idClienteOrigen) {
      navigate('/clientes', { state: { idAbrir: idClienteOrigen } });
    } else {
      setDetalle(null);
    }
  };

  const reabrir = async (id) => {
    await api(`/ordenes/${id}/reabrir`, { method: 'POST' });
    cargar();
    setDetalle(null);
    setEditando(id);
  };

  const eliminarOrden = async () => {
    await api(`/ordenes/${detalle.id}`, { method: 'DELETE' });
    setConfirmandoEliminar(false);
    setDetalle(null);
    cargar();
  };

  const eliminarDeLista = async () => {
    await api(`/ordenes/${eliminandoDeLista}`, { method: 'DELETE' });
    setEliminandoDeLista(null);
    cargar();
  };

  if (editando) return (
    <EditarOrden idOrden={editando}
      onGuardado={() => { cargar(); verDetalle(editando); setEditando(null); }}
      onCancelar={() => { cargar(); verDetalle(editando); setEditando(null); }} />
  );

  if (cerrando) return (
    <CerrarOrden idOrden={cerrando}
      onCerrada={() => { cargar(); verDetalle(cerrando); setCerrando(null); }}
      onCancelar={() => setCerrando(null)} />
  );

  if (detalle) return (
    <div className="page-content">
      <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <button className="btn btn-secondary btn-sm" onClick={volver}>← Volver</button>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 600 }}>{detalle.id}</h2>
        <StatusBadge status={detalle.status} />
        <button className="btn btn-outline btn-sm" style={{ color: 'var(--rojo)', marginLeft: 'auto' }}
          onClick={() => setConfirmandoEliminar(true)}>🗑️ Eliminar</button>
      </div>

      {confirmandoEliminar && (
        <ConfirmModal
          mensaje="¿Eliminar esta orden? Quedará oculta de las listas, pero se puede recuperar si hace falta."
          textoConfirmar="Eliminar"
          onConfirm={eliminarOrden}
          onCancel={() => setConfirmandoEliminar(false)}
        />
      )}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="resumen-grid">
          <div className="resumen-card"><div className="label">Cliente</div><div className="valor">{detalle.nombre_cliente}</div></div>
          <div className="resumen-card"><div className="label">Vehículo</div><div className="valor">{detalle.marca} {detalle.modelo}</div></div>
          <div className="resumen-card"><div className="label">Matrícula</div><div className="valor">{detalle.matricula || '—'}</div></div>
          <div className="resumen-card"><div className="label">Fecha</div><div className="valor">{fmtFecha(detalle.fecha)}</div></div>
          <div className="resumen-card"><div className="label">Km</div><div className="valor">{detalle.kilometraje ? `${detalle.kilometraje.toLocaleString()} km` : '— (se completa al cerrar)'}</div></div>
          <div className="resumen-card"><div className="label">Total</div><div className="valor" style={{ color: 'var(--azul)' }}>${(detalle.total || 0).toLocaleString()}</div></div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, color: 'var(--azul)', marginBottom: '.6rem' }}>Servicios realizados</p>
        {detalle.paquetes.map((p, i) => (
          <div key={i} style={{ marginBottom: '.75rem', paddingBottom: '.75rem',
                                borderBottom: i < detalle.paquetes.length - 1 ? '1px solid var(--gris-2)' : 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '.4rem' }}>
              <span style={{ fontWeight: 600 }}>{p.nombre}</span>
              <span style={{ fontWeight: 700 }}>${(p.precio || 0).toLocaleString()}</span>
            </div>
            {p.tareas.map((t, j) => (
              <p key={j} style={{ fontSize: '.8125rem', color: 'var(--gris-5)', marginBottom: '.15rem' }}>• {t.descripcion}</p>
            ))}
            {p.repuestos.length > 0 && (
              <p style={{ fontSize: '.8125rem', color: 'var(--gris-4)', marginTop: '.3rem', fontStyle: 'italic' }}>
                Repuestos: {p.repuestos.map(r => r.nombre + (r.detalle ? ` (${r.detalle})` : '')).join(', ')}
              </p>
            )}
          </div>
        ))}
      </div>

      {detalle.observaciones && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <p style={{ fontWeight: 600, color: 'var(--azul)', marginBottom: '.5rem' }}>Observaciones</p>
          <p style={{ fontSize: '.875rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{detalle.observaciones}</p>
        </div>
      )}

      <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        {detalle.status === 'borrador' && (
          <>
            <a href={apiUrl(`/descargas/ordenes/${detalle.id}/hoja-trabajo`)} className="btn btn-outline btn-sm" target="_blank" rel="noreferrer">
              🖨️ Hoja de trabajo
            </a>
            <button className="btn btn-outline btn-sm" disabled={enviandoEmail || emailEnviado} onClick={() => enviarPorEmail('borrador')}>
              {emailEnviado ? '✅ Enviado' : enviandoEmail ? 'Enviando...' : '📧 Enviar por email'}
            </button>
            <button className="btn btn-outline btn-sm" onClick={() => setEditando(detalle.id)}>
              ✏️ Editar servicios
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => setCerrando(detalle.id)}>
              ✅ Aceptar y cerrar orden
            </button>
          </>
        )}
        {detalle.status === 'cerrada' && (
          <>
            <a href={apiUrl(`/descargas/ordenes/${detalle.id}/excel`)} className="btn btn-primary btn-sm" download>
              📥 Descargar Excel
            </a>
            <a href={apiUrl(`/descargas/ordenes/${detalle.id}/pdf`)} className="btn btn-success btn-sm" target="_blank" rel="noreferrer">
              📄 Ver PDF
            </a>
            <button className="btn btn-outline btn-sm" disabled={enviandoEmail || emailEnviado} onClick={() => enviarPorEmail('cerrada')}>
              {emailEnviado ? '✅ Enviado' : enviandoEmail ? 'Enviando...' : '📧 Enviar por email'}
            </button>
            <button className="btn btn-outline btn-sm" onClick={() => reabrir(detalle.id)}>
              🔓 Reabrir para corregir
            </button>
          </>
        )}
      </div>
    </div>
  );

  const q = busqueda.trim().toLowerCase();
  const ordenesFiltradas = !q ? ordenes : ordenes.filter(o => (
    o.id.toLowerCase().includes(q) ||
    (o.nombre_cliente || '').toLowerCase().includes(q) ||
    (o.marca || '').toLowerCase().includes(q) ||
    (o.modelo || '').toLowerCase().includes(q) ||
    (o.matricula || '').toLowerCase().includes(q)
  ));
  const ordenesAbiertas = ordenesFiltradas.filter(o => o.status !== 'cerrada');
  const ordenesCerradas = ordenesFiltradas.filter(o => o.status === 'cerrada');

  const filaOrden = (o) => (
    <tr key={o.id}>
      <td><span className="badge badge-gris">{o.id}</span></td>
      <td>{fmtFecha(o.fecha)}</td>
      <td style={{ fontWeight: 500 }}>{o.nombre_cliente}</td>
      <td>{o.marca} {o.modelo} {o.matricula ? `| ${o.matricula}` : ''}</td>
      <td>${(o.total || 0).toLocaleString()}</td>
      <td><StatusBadge status={o.status} /></td>
      <td style={{ display: 'flex', gap: '.4rem' }}>
        <button className="btn btn-outline btn-sm" onClick={() => verDetalle(o.id)}>Ver</button>
        <button className="btn btn-outline btn-sm" style={{ color: 'var(--rojo)' }}
          onClick={() => setEliminandoDeLista(o.id)}>🗑️</button>
      </td>
    </tr>
  );

  return (
    <div className="page-content">
      <h1 className="page-title">Órdenes de trabajo</h1>

      {ordenes.length > 0 && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div className="search-wrap">
            <span className="search-icon">🔍</span>
            <input className="form-input" placeholder="Buscar por cliente, marca, modelo, matrícula o N° de orden..."
              value={busqueda} onChange={e => setBusqueda(e.target.value)} />
          </div>
        </div>
      )}

      {loading ? <Spinner texto="Cargando..." /> :
        ordenes.length === 0 ? <Empty icon="📋" texto="Sin órdenes" /> :
        ordenesFiltradas.length === 0 ? <Empty icon="🔍" texto="No hay órdenes que coincidan con la búsqueda" /> :
        <>
          {ordenesAbiertas.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ fontWeight: 600, color: 'var(--azul)', marginBottom: '.5rem' }}>Abiertas ({ordenesAbiertas.length})</p>
              <div className="tabla-wrap">
                <table className="tabla">
                  <thead>
                    <tr><th>ID</th><th>Fecha</th><th>Cliente</th><th>Vehículo</th><th>Total</th><th>Estado</th><th></th></tr>
                  </thead>
                  <tbody>
                    {ordenesAbiertas.map(filaOrden)}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {ordenesCerradas.length > 0 && (
            <div>
              <p style={{ fontWeight: 600, color: 'var(--gris-4)', marginBottom: '.5rem' }}>Cerradas ({ordenesCerradas.length})</p>
              <div className="tabla-wrap">
                <table className="tabla">
                  <thead>
                    <tr><th>ID</th><th>Fecha</th><th>Cliente</th><th>Vehículo</th><th>Total</th><th>Estado</th><th></th></tr>
                  </thead>
                  <tbody>
                    {ordenesCerradas.map(filaOrden)}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      }

      {eliminandoDeLista && (
        <ConfirmModal
          mensaje="¿Eliminar esta orden? Quedará oculta de las listas, pero se puede recuperar si hace falta."
          textoConfirmar="Eliminar"
          onConfirm={eliminarDeLista}
          onCancel={() => setEliminandoDeLista(null)}
        />
      )}
    </div>
  );
}
